import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Clock, Inbox, Loader2, Plus, Trash2, Upload, XCircle } from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import {
  annoncerErreur, annoncerSucces, confirmer, EtatErreur, EtatSansDonnees, SqueletteTexte,
  SuiviTeleversement, useTeleversement, validerFichier,
} from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   LES PIÈCES DEMANDÉES À L'ORGANISATION

   Le coach (ou l'administration) nomme la pièce attendue et, s'il le veut,
   sa forme : PDF, image, tableur… L'organisation en est notifiée et voit la
   liste de ce qui reste à fournir. Le format est vérifié au dépôt.

   Le coach peut aussi déposer la pièce à la place de l'organisation — un
   document reçu par courriel, par exemple. Cela se voit des deux côtés.
   ═══════════════════════════════════════════════════════════════════════ */

const TYPES = [
  ['', 'Tout format'], ['pdf', 'PDF'], ['image', 'Image'], ['tableur', 'Tableur (Excel, CSV)'],
  ['texte', 'Document texte (Word)'], ['presentation', 'Présentation'], ['archive', 'Archive (zip)'],
];

const ETAT = {
  a_fournir: { label: 'À fournir', Icone: Clock, classe: 'text-[var(--attention)]' },
  soumis: { label: 'Fourni, à valider', Icone: Clock, classe: 'text-[var(--text-bold)]' },
  valide: { label: 'Validé', Icone: CheckCircle2, classe: 'text-[var(--accent-texte)]' },
  rejete: { label: 'Rejeté, à refaire', Icone: XCircle, classe: 'text-[var(--alerte)]' },
};

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// Les mêmes bornes que le serveur (`_validate_upload`) : refuser AVANT
// d'envoyer plutôt qu'après une minute de transfert.
const FORMATS_ACCEPTES = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.txt', '.csv', '.zip'];
const TAILLE_MAX = 10 * 1024 * 1024;

function DeposerALaPlace({ projetId, demande, onDepose }) {
  const ref = useRef(null);
  const envoi = useTeleversement();
  const [nom, setNom] = useState('');
  const extensions = demande?.extensions?.length ? demande.extensions : FORMATS_ACCEPTES;

  const envoyer = async (fichier) => {
    if (!fichier) return;
    if (ref.current) ref.current.value = '';
    const probleme = validerFichier(fichier, { extensions, tailleMax: TAILLE_MAX });
    if (probleme) { annoncerErreur(probleme); return; }
    const fd = new FormData();
    fd.append('projet_id', projetId);
    fd.append('categorie', 'autre');
    fd.append('nom_fichier', demande?.libelle || fichier.name);
    if (demande) fd.append('demande_id', demande.id);
    fd.append('fichier', fichier);
    setNom(fichier.name);
    try {
      await envoi.lancer({ url: `${API_BASE}/accompagnement/documents/`, donnees: fd });
      annoncerSucces(`« ${fichier.name} » déposé pour l’organisation.`);
      envoi.reinitialiser();
      onDepose();
    } catch (e) {
      if (!e.annule) annoncerErreur(e.message || 'Le dépôt n’a pas abouti.');
      envoi.reinitialiser();
    }
  };

  return (
    <span className="inline-flex flex-col gap-1.5 min-w-[160px]">
      <input ref={ref} type="file" className="hidden" accept={extensions.join(',')}
             onChange={(e) => envoyer(e.target.files[0])} />
      {envoi.enCours ? (
        <SuiviTeleversement televersement={envoi} nom={nom} />
      ) : (
        <button type="button" onClick={() => ref.current?.click()}
                className="act act-sm act-contour" title="Déposer à la place de l'organisation">
          <Upload className="w-3.5 h-3.5" />
          {demande ? 'Déposer à sa place' : 'Déposer un document'}
        </button>
      )}
    </span>
  );
}

export default function DocumentsDemandesProjet({ projetId, peutAgir, onChange }) {
  // `null` tant que la première réponse n'est pas arrivée.
  const [demandes, setDemandes] = useState(null);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [form, setForm] = useState({ libelle: '', type_attendu: '', consigne: '' });
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    try {
      const d = await adminApi.getDocumentsDemandes(projetId);
      setDemandes(Array.isArray(d) ? d : []);
      setErreurChargement(null);
    } catch (e) {
      // L'onglet reste utilisable sans la liste : l'échec se dit à sa place.
      setErreurChargement(e);
    }
  }, [projetId]);
  useEffect(() => { charger(); }, [charger]);

  const rafraichir = () => { charger(); onChange?.(); };

  const demander = async (e) => {
    e.preventDefault();
    if (!form.libelle.trim()) return;
    setEnCours(true);
    setErreur('');
    try {
      await adminApi.demanderDocument(projetId, { ...form, libelle: form.libelle.trim() });
      annoncerSucces(`« ${form.libelle.trim()} » demandé : l’organisation est prévenue.`);
      setForm({ libelle: '', type_attendu: '', consigne: '' });
      setOuvert(false);
      rafraichir();
    } catch (err) {
      setErreur(err?.message || 'La demande n’a pas été enregistrée.');
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async (d) => {
    if (!await confirmer(`Retirer la demande « ${d.libelle} » ? Les fichiers déjà déposés restent.`)) return;
    try {
      await adminApi.supprimerDemandeDocument(d.id);
      annoncerSucces(`Demande « ${d.libelle} » retirée.`);
      rafraichir();
    } catch (e) {
      annoncerErreur(e?.message || 'La demande n’a pas pu être retirée.');
    }
  };

  return (
    <section className="bg-[var(--bg-card)] rounded-carte shadow-carte p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <h3 className="titre-carte flex items-center gap-2"><Inbox className="w-4 h-4 text-[var(--accent-texte)]" />Pièces demandées</h3>
        {peutAgir && (
          <div className="flex items-center gap-2 flex-wrap">
            <DeposerALaPlace projetId={projetId} demande={null} onDepose={rafraichir} />
            <button type="button" onClick={() => setOuvert((o) => !o)} className="act act-sm">
              <Plus className="w-3.5 h-3.5" /> Demander un document
            </button>
          </div>
        )}
      </div>

      {ouvert && (
        <form onSubmit={demander} className="grid sm:grid-cols-2 gap-3 mb-4 rounded-pastille border border-[var(--border)] p-4">
          <label className="sm:col-span-2">
            <span className="block surtitre text-[var(--text-muted)] mb-1">Pièce attendue *</span>
            <input className="champ" value={form.libelle} maxLength={200} required
                   placeholder="Statuts signés, budget 2026…"
                   onChange={(e) => setForm((f) => ({ ...f, libelle: e.target.value }))} />
          </label>
          <label>
            <span className="block surtitre text-[var(--text-muted)] mb-1">Format (optionnel)</span>
            <select className="champ" value={form.type_attendu}
                    onChange={(e) => setForm((f) => ({ ...f, type_attendu: e.target.value }))}>
              {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label>
            <span className="block surtitre text-[var(--text-muted)] mb-1">Consigne (optionnel)</span>
            <input className="champ" value={form.consigne}
                   onChange={(e) => setForm((f) => ({ ...f, consigne: e.target.value }))} />
          </label>
          {erreur && <p className="sm:col-span-2 encart encart-alerte">{erreur}</p>}
          <div className="sm:col-span-2 flex gap-2">
            <button type="submit" disabled={enCours} aria-busy={enCours || undefined} className="act">
              {enCours && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {enCours ? 'Envoi…' : 'Demander et prévenir l’organisation'}
            </button>
            <button type="button" onClick={() => setOuvert(false)} className="act act-contour">Annuler</button>
          </div>
        </form>
      )}

      {demandes === null ? (
        erreurChargement
          ? <EtatErreur erreur={erreurChargement} onReessayer={charger} compact />
          : <SqueletteTexte lignes={3} className="py-2" />
      ) : demandes.length === 0 ? (
        <EtatSansDonnees compact titre="Aucune pièce demandée pour l’instant"
                         aide={peutAgir ? 'Demandez un document : l’organisation est prévenue et le dépose depuis son espace.' : undefined}
                         action={peutAgir && (
                           <button type="button" onClick={() => setOuvert(true)} className="act act-sm">
                             <Plus className="w-3.5 h-3.5" /> Demander un document
                           </button>
                         )} />
      ) : (
        <ul className="divide-y divide-[var(--border)]">
          {demandes.map((d) => {
            const etat = ETAT[d.etat] || ETAT.a_fournir;
            const { Icone } = etat;
            return (
              <li key={d.id} className="py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-[1_1_240px]">
                  <p className="text-[13.5px] font-semibold text-[var(--text-bold)]">{d.libelle}</p>
                  <p className="text-[12px] text-[var(--text-muted)]">
                    {d.type_attendu ? d.type_libelle : 'Tout format'}
                    {d.consigne ? ` · ${d.consigne}` : ''}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1 text-[12px] font-semibold ${etat.classe}`}>
                  <Icone className="w-3.5 h-3.5" />{etat.label}
                </span>
                {peutAgir && (d.etat === 'a_fournir' || d.etat === 'rejete') && (
                  <DeposerALaPlace projetId={projetId} demande={d} onDepose={rafraichir} />
                )}
                {peutAgir && (
                  <button type="button" onClick={() => retirer(d)} className="act act-icone act-tertiaire" aria-label="Retirer la demande">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
